import React, { useEffect, useRef, useState } from "react";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Box, Typography, Button, Divider } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import { useCart } from "../contexts/cartContext";
import { useAuth } from "../contexts/authContext";
import { saveOrder } from "../api/orders";
import type { CartItem } from "../contexts/cartContext";
import { capFirst } from "../utils/groupVariants";

const PurchasePage = () => {
  const { items, cartTotal, clearCart } = useCart();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [purchasedItems, setPurchasedItems] = useState<CartItem[]>([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [pendingOrder, setPendingOrder] = useState<{
    sessionId: string;
    items: CartItem[];
    total: number;
  } | null>(null);
  const initialized = useRef(false);
  const orderSaved = useRef(false);

  // Snapshot cart and clear it immediately on mount
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const valid = sessionStorage.getItem("checkout_pending");
    if (!valid) {
      navigate("/");
      return;
    }
    sessionStorage.removeItem("checkout_pending");

    const snapshot = [...items];
    const total = cartTotal;
    setPurchasedItems(snapshot);
    setOrderTotal(total);

    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setPendingOrder({ sessionId, items: snapshot, total });
    }

    clearCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save order once session is available (Supabase restores it async after redirect)
  useEffect(() => {
    if (!pendingOrder || !session?.access_token || orderSaved.current) return;
    orderSaved.current = true;
    saveOrder(
      pendingOrder.sessionId,
      pendingOrder.items,
      pendingOrder.total,
      session.access_token
    ).catch(() => {});
  }, [session, pendingOrder]);

  return (
    <Box
      sx={{
        maxWidth: 560,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 8, md: 12 },
      }}
    >
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "black", mb: 2 }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            mb: 1,
          }}
        >
          Order Confirmed
        </Typography>
        <Typography variant="body2" sx={{ color: "#737373" }}>
          Thank you for your purchase. You&apos;ll receive a confirmation email
          shortly.
        </Typography>
      </Box>

      {purchasedItems.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              fontWeight: 700,
              color: "#737373",
              display: "block",
              mb: 2,
            }}
          >
            Order Summary
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {purchasedItems.map((item) => {
              const price = item.variant.salePrice ?? 0;
              return (
                <Box
                  key={item.variant.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "56px 1fr auto",
                    gap: 2,
                    py: 2,
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  <Box
                    component="img"
                    src={item.variant.image}
                    alt={item.variant.name}
                    sx={{
                      width: 56,
                      height: 70,
                      objectFit: "cover",
                      bgcolor: "#f5f5f5",
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {capFirst(item.variant.name)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#737373", display: "block" }}
                    >
                      Size: {item.variant.size}
                      {item.quantity > 1 && ` · Qty: ${item.quantity}`}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${(price * item.quantity).toFixed(2)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 2,
              mt: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Total
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              ${orderTotal.toFixed(2)}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />
        </>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Button
          component={RouterLink}
          to="/orders"
          variant="outlined"
          fullWidth
          sx={{
            borderColor: "#d4d4d4",
            color: "black",
            py: 1.5,
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            "&:hover": { borderColor: "black" },
          }}
        >
          View Order History
        </Button>
        <Button
          component={RouterLink}
          to="/"
          variant="contained"
          fullWidth
          sx={{
            bgcolor: "black",
            color: "white",
            py: 1.75,
            fontSize: "0.75rem",
            letterSpacing: "0.12em",
            "&:hover": { bgcolor: "#333" },
          }}
        >
          Continue Shopping
        </Button>
      </Box>
    </Box>
  );
};

export default PurchasePage;
